import React, { useEffect, useState } from 'react'
import { Avatar, Button, Chip, IconButton, Modal, Box, Typography, Grid, Card, CardMedia, CardContent, CardActions } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Description as DocumentIcon } from '@mui/icons-material';
import { Input, Select } from '../Form';
import ModalBox from '../modal/ModalBox';
import ImagePreview from './ImagePreview';
import VideoPreview from '../videoPreview/VideoPreview';
import { toastMessage } from '../../utils/toastMessage';
import { regex } from '../../utils/regex';
import { checkImageAspectRatio, checkImageDimensions } from '../../utils/checkImageDimension';
import commonMessages from '../../utils/commonMessages';
import { 
    fetchConsultantGalleryApi, 
    uploadConsultantMediaApi, 
    approveConsultantMediaApi, 
    deleteConsultantMediaApi 
} from '../../services/imageManagement';

const { imageFileRegex, videoFileRegex } = regex;
const { IMAGE_FORMAT, VIDEO_FORMAT, MESSAGE_SIZE, EXCEED_SIZE } = commonMessages;

// Document file regex
const documentFileRegex = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)$/i;

export default function ConsultantProfileAssests({ consultantID, consultantDetail = {} }) {
    const [galleryData, setGalleryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState('profile');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [newMedia, setNewMedia] = useState(null);
    const [mediaType, setMediaType] = useState('image');
    const [error, setError] = useState({});
    const [videoRef, setVideoRef] = useState(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const normalizeMediaUrl = (value) => {
        if (!value) return "";
        if (typeof value === "string") return value;
        return value.image || value.profileImage || value.url || value.path || "";
    };

    const buildFallbackGallery = () => {
        const fallbackItems = [];
        const addFallbackMedia = (value, imageType = "image", index = 0) => {
            const image = normalizeMediaUrl(value);
            if (!image) return;
            fallbackItems.push({
                _id: `consultant-request-${selectedType}-${fallbackItems.length}-${index}`,
                image,
                imageType,
                isApprove: true,
                isFallback: true,
                createdAt: consultantDetail.updatedAt || consultantDetail.createdAt || new Date().toISOString(),
            });
        };

        if (selectedType === "profile") {
            addFallbackMedia(consultantDetail.profileImage, "image");
            const galleryPhotos = Array.isArray(consultantDetail.galleryPhotos)
                ? consultantDetail.galleryPhotos
                : [];
            galleryPhotos.forEach((item, index) => addFallbackMedia(item, "image", index));
            const legacyGallery = Array.isArray(consultantDetail.gallery)
                ? consultantDetail.gallery
                : [];
            legacyGallery.forEach((item, index) => addFallbackMedia(item, "image", index));
        }

        if (selectedType === "live") {
            addFallbackMedia(consultantDetail.videoIntroduction, "video");
        }

        return fallbackItems;
    };

    useEffect(() => {
        if (consultantID) {
            fetchGalleryData();
        }
    }, [consultantID, selectedType]);

    // Cleanup video when component unmounts or modal closes
    useEffect(() => {
        return () => {
            if (videoRef && isVideoPlaying) {
                videoRef.pause();
                setIsVideoPlaying(false);
            }
        };
    }, [videoRef, isVideoPlaying]);

    const fetchGalleryData = async () => {
        try {
            setLoading(true);
            const response = await fetchConsultantGalleryApi(consultantID, selectedType);
            
            if (response.data.code === 200) {
                setGalleryData(response.data.result);
            } else {
                toastMessage(response.data.message || 'Failed to fetch gallery data', 'error');
            }
        } catch (error) {
            console.error('Error fetching gallery data:', error);
            toastMessage('Failed to fetch gallery data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleTypeChange = (type) => {
        setSelectedType(type);
    };

    const handleAddMedia = () => {
        setAddModalOpen(true);
        setNewMedia(null);
        setMediaType('image');
        setError({});
    };

    const handleMediaUpload = async (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (!selectedFiles.length) return;

        const isValidFile = (file) => {
            if (mediaType === 'image') return imageFileRegex.test(file.name.toLowerCase());
            if (mediaType === 'video') return videoFileRegex.test(file.name.toLowerCase());
            return documentFileRegex.test(file.name.toLowerCase());
        };

        if (selectedFiles.some((file) => !isValidFile(file))) {
            e.target.value = '';
            setNewMedia(null);
            toastMessage(`Please select valid ${mediaType} file(s)`, 'error');
            return;
        }

        setError({});
        setNewMedia(mediaType === 'image' ? selectedFiles : selectedFiles[0]);
    };

    const handleSaveMedia = async () => {
        console.log("handleSaveMedia called");
        console.log("newMedia state:", newMedia);
        console.log("selectedType state:", selectedType);
        console.log("consultantID prop:", consultantID);
        
        if (!newMedia) {
            setError({ media: 'Please select a file' });
            return;
        }

        try {
            const formData = new FormData();

            const mediaFiles = Array.isArray(newMedia) ? newMedia : [newMedia];
            mediaFiles.forEach((file) => formData.append('images', file));
            formData.append('galleryType', selectedType);
            formData.append('consultantId', consultantID);

            // Debug FormData contents properly
            console.log("newMedia ===>", newMedia);
            console.log("selectedType ===>", selectedType);
            console.log("consultantID ===>", consultantID);
            
            // Log FormData entries to see actual contents
            for (let [key, value] of formData.entries()) {
                console.log(`FormData ${key}:`, value);
            }
            
            console.log("formData ===>", formData);

            const response = await uploadConsultantMediaApi(formData);

            if (response.data.code === 200) {
                toastMessage('Media uploaded successfully', 'success');
                setAddModalOpen(false);
                fetchGalleryData();
            } else {
                toastMessage(response.data.message || 'Failed to upload media', 'error');
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            toastMessage('Failed to upload media', 'error');
        }
    };

    const handlePreviewMedia = (media) => {
        setSelectedMedia(media);
        setPreviewModalOpen(true);
        setIsVideoPlaying(false);
    };

    const handleVideoPlay = () => {
        if (videoRef) {
            videoRef.play();
            setIsVideoPlaying(true);
        }
    };

    const handleVideoPause = () => {
        if (videoRef) {
            videoRef.pause();
            setIsVideoPlaying(false);
        }
    };

    const handleModalClose = () => {
        if (videoRef && isVideoPlaying) {
            videoRef.pause();
            setIsVideoPlaying(false);
        }
        setPreviewModalOpen(false);
        setSelectedMedia(null);
        setVideoRef(null);
    };

    const handleApproveMedia = async (mediaId) => {
        try {
            const response = await approveConsultantMediaApi({
                consultantId: consultantID,
                galleryType: selectedType,
                imagesId: [mediaId]
            });

            if (response.data.code === 200) {
                toastMessage('Media approved successfully', 'success');
                fetchGalleryData();
            } else {
                toastMessage(response.data.message || 'Failed to approve media', 'error');
            }
        } catch (error) {
            console.error('Error approving media:', error);
            toastMessage('Failed to approve media', 'error');
        }
    };

    const handleDeleteMedia = async (mediaId) => {
        try {
            const response = await deleteConsultantMediaApi(mediaId);

            if (response.data.code === 200) {
                toastMessage('Media deleted successfully', 'success');
                fetchGalleryData();
            } else {
                toastMessage(response.data.message || 'Failed to delete media', 'error');
            }
        } catch (error) {
            console.error('Error deleting media:', error);
            toastMessage('Failed to delete media', 'error');
        }
    };

    const renderMediaCard = (media) => {
        const isVideo = media.image.match(/\.(mp4|avi|mov|wmv|webm|m4v)(\?.*)?$/i) || media.imageType === "video";
        const isDocument = media.image.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)$/i);
        const approvalStatus = media.approvalStatus || (media.isApprove ? 'approved' : 'pending');
        const statusLabel = approvalStatus === 'approved' ? 'Approved' : approvalStatus === 'rejected' ? 'Rejected' : 'Pending';
        const statusColor = approvalStatus === 'approved' ? 'success' : approvalStatus === 'rejected' ? 'error' : 'warning';
        
        return (
            <Card key={media._id} sx={{ 
                maxWidth: 300, 
                m: 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                },
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {isDocument ? (
                    <Box
                        sx={{
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#e9ecef',
                            }
                        }}
                        onClick={() => handlePreviewMedia(media)}
                    >
                        <DocumentIcon sx={{ fontSize: 60, color: '#FF8989', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
                            {media.image.split('/').pop().split('?')[0]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Click to view/download
                        </Typography>
                    </Box>
                ) : (
                    <CardMedia
                        component={isVideo ? 'video' : 'img'}
                        height="200"
                        image={media.image}
                        alt="Media"
                        sx={{ 
                            cursor: 'pointer', 
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.05)',
                            }
                        }}
                        onClick={() => handlePreviewMedia(media)}
                    />
                )}
                {isVideo && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            borderRadius: '50%',
                            width: 50,
                            height: 50,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    >
                        <Box
                            sx={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderLeft: '12px solid white',
                                marginLeft: '2px'
                            }}
                        />
                    </Box>
                )}
                <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Chip
                            label={statusLabel}
                            color={statusColor}
                            size="small"
                            sx={{ 
                                fontWeight: 600,
                                fontSize: '0.75rem'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {new Date(media.createdAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                </CardContent>
                <CardActions sx={{ 
                    p: 1, 
                    pt: 0, 
                    justifyContent: 'space-around',
                    backgroundColor: '#f8f9fa'
                }}>
                    <IconButton
                        size="small"
                        onClick={() => handlePreviewMedia(media)}
                        title="Preview"
                        sx={{ 
                            color: '#6c757d',
                            '&:hover': { color: '#FF8989' }
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                    {!media.isFallback && approvalStatus !== 'approved' && (
                        <IconButton
                            size="small"
                            onClick={() => handleApproveMedia(media._id)}
                            title="Approve"
                            color="success"
                            sx={{ 
                                '&:hover': { 
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)' 
                                }
                            }}
                        >
                            <CheckCircleIcon />
                        </IconButton>
                    )}
                    {!media.isFallback && (
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteMedia(media._id)}
                            title="Delete"
                            color="error"
                            sx={{ 
                                '&:hover': { 
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)' 
                                }
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </CardActions>
            </Card>
        );
    };

    const fallbackGallery = buildFallbackGallery();
    const fetchedGallery = galleryData?.list?.gallery || [];
    const fetchedUrls = new Set(fetchedGallery.map((item) => item.image));
    const displayedGallery = [
        ...fetchedGallery,
        ...fallbackGallery.filter((item) => !fetchedUrls.has(item.image)),
    ];

    return (
        <div className="WrapperArea" style={{ marginLeft: '10px' }}>
            <div className="WrapperBox">
                <div className="TitleBox">
                    <h4 className="Title">Consultant Profile Assets</h4>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddMedia}
                        sx={{ 
                            backgroundColor: '#FF8989', 
                            '&:hover': { backgroundColor: '#e67e7e' },
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Add Media
                    </Button>
                </div>

                <div className="FilterBox FilterArea">
                    <div className="banner_wrapper">
                        <div className="banner_button_wrapper">
                            <button
                                className={`_button ${selectedType === 'profile' && 'banner_active'}`}
                                onClick={() => handleTypeChange('profile')}
                            >
                                Profile Gallery
                            </button>
                            <button
                                className={`_button ${selectedType === 'live' && 'banner_active'}`}
                                onClick={() => handleTypeChange('live')}
                            >
                                Live Gallery
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : galleryData && galleryData.list ? (
                    <div className="content-area">
                

                        {displayedGallery.length > 0 ? (
                            <Grid container spacing={3}>
                                {displayedGallery.map((media) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={media._id}>
                                        {renderMediaCard(media)}
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <div className="text-center p-5" style={{ 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '12px',
                                border: '2px dashed #dee2e6'
                            }}>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                                    No media found for this consultant
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Click "Add Media" to upload images or videos
                                </Typography>
                            </div>
                        )}
                    </div>
                ) : displayedGallery.length > 0 ? (
                    <div className="content-area">
                        <Grid container spacing={3}>
                            {displayedGallery.map((media) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={media._id}>
                                    {renderMediaCard(media)}
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                ) : (
                    <div className="text-center p-5" style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '12px',
                        border: '2px dashed #dee2e6'
                    }}>
                        <Typography variant="h6" color="text.secondary">
                            No consultant data found
                        </Typography>
                    </div>
                )}
            </div>

            {/* Add Media Modal */}
            <ModalBox
                status={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                title="Add New Media"
                size="md"
                closeIcon
                custom
                customizedFooter={
                    <div className="model_custom_footer">
                        <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                            Uploaded media will be processed. 
                            <br />
                            <strong>Images:</strong> JPG, JPEG, PNG, GIF formats
                            <br />
                            <strong>Videos:</strong> MP4, AVI, MOV, WMV formats  
                            <br />
                            <strong>Documents:</strong> PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF formats
                            <br />
                            Maximum file size: 10MB.
                        </p>
                        <button
                            className="btn_size"
                            onClick={handleSaveMedia}
                            style={{ 
                                backgroundColor: '#FF8989', 
                                border: 'none',
                                borderRadius: '6px',
                                padding: '10px 20px',
                                color: 'white',
                                fontWeight: 600
                            }}
                        >
                            Save
                        </button>
                    </div>
                }
            >
                <div>
                    <div className="form-group mb-3">
                        <Select
                            name="mediaType"
                            value={mediaType}
                            onChange={(e) => {
                                setMediaType(e.target.value);
                                setNewMedia(null);
                                setError({});
                            }}
                            label="Media Type"
                            className="form-control"
                        >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            {/* <option value="document">Document</option> */}
                        </Select>
                    </div>

                    <div className="form-group mb-3">
                        <Input
                            type="file"
                            accept={
                                mediaType === 'image' 
                                    ? 'image/*' 
                                    : mediaType === 'video' 
                                        ? 'video/*' 
                                        : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf'
                            }
                            multiple={mediaType === 'image'}
                            name="media"
                            onChange={handleMediaUpload}
                            placeholder={`Upload ${mediaType}`}
                            className="form-control"
                            label={`Upload ${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}`}
                            error={error.media}
                        />
                    </div>

                    {newMedia && (
                        <div className="media-preview" style={{ 
                            border: '2px solid #e9ecef', 
                            borderRadius: '8px', 
                            padding: '15px',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Preview:
                            </Typography>
                            {mediaType === 'image' ? (
                                <Grid container spacing={2}>
                                    {(Array.isArray(newMedia) ? newMedia : [newMedia]).map((file) => (
                                        <Grid item xs={6} key={`${file.name}-${file.lastModified}`}>
                                            <ImagePreview imageUrl={URL.createObjectURL(file)} />
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : mediaType === 'video' ? (
                                <VideoPreview videoUrl={URL.createObjectURL(newMedia)} />
                            ) : mediaType === 'document' ? (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <DocumentIcon sx={{ fontSize: 60, color: '#FF8989', mb: 2 }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                                        {newMedia.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        File size: {(newMedia.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Document will be uploaded and available for download
                                    </Typography>
                                </Box>
                            ) : null}
                        </div>
                    )}
                </div>
            </ModalBox>

            {/* Preview Modal */}
            <Modal
                open={previewModalOpen}
                onClose={handleModalClose}
                aria-labelledby="media-preview-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 4,
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        borderRadius: '12px',
                    }}
                >
                    <IconButton
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'grey.500',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            }
                        }}
                        onClick={handleModalClose}
                    >
                        <CancelIcon />
                    </IconButton>
                    {selectedMedia && (
                        <Box>
                            {selectedMedia.image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <img
                                    src={selectedMedia.image}
                                    alt="Preview"
                                    style={{ 
                                        maxWidth: '100%', 
                                        height: 'auto',
                                        borderRadius: '8px'
                                    }}
                                />
                            ) : selectedMedia.image.match(/\.(mp4|avi|mov|wmv)$/i) ? (
                                <Box sx={{ position: 'relative' }}>
                                    <video
                                        ref={(el) => setVideoRef(el)}
                                        controls
                                        style={{ 
                                            maxWidth: '100%', 
                                            height: 'auto',
                                            borderRadius: '8px'
                                        }}
                                        onPlay={() => setIsVideoPlaying(true)}
                                        onPause={() => setIsVideoPlaying(false)}
                                        onEnded={() => setIsVideoPlaying(false)}
                                    >
                                        <source src={selectedMedia.image} type={`video/${selectedMedia.image.split('.').pop()}`} />
                                        Your browser does not support the video tag.
                                    </video>
                                    {!isVideoPlaying && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                cursor: 'pointer',
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                borderRadius: '50%',
                                                width: 60,
                                                height: 60,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                                }
                                            }}
                                            onClick={handleVideoPlay}
                                        >
                                            <Box
                                                sx={{
                                                    width: 0,
                                                    height: 0,
                                                    borderTop: '12px solid transparent',
                                                    borderBottom: '12px solid transparent',
                                                    borderLeft: '20px solid white',
                                                    marginLeft: '4px'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ) : selectedMedia.image.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)$/i) ? (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    p: 4,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '2px dashed #dee2e6'
                                }}>
                                    <DocumentIcon sx={{ fontSize: 80, color: '#FF8989', mb: 3 }} />
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        {selectedMedia.image.split('/').pop().split('?')[0]}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        This document cannot be previewed directly. Click the button below to download.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<DocumentIcon />}
                                        onClick={() => window.open(selectedMedia.image, '_blank')}
                                        sx={{ 
                                            backgroundColor: '#FF8989', 
                                            '&:hover': { backgroundColor: '#e67e7e' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Download Document
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    p: 4,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px'
                                }}>
                                    <Typography variant="h6" color="text.secondary">
                                        Unsupported file type
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => window.open(selectedMedia.image, '_blank')}
                                        sx={{ mt: 2 }}
                                    >
                                        Download File
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Modal>
        </div>
    );
}
